import { clsx } from 'clsx';
import { Divider, IconButton, Icon, Tooltip, icon } from '@nula/core-ui';
import type { NodeType } from '@/features/canvas/utils/node-types';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import { canvasShortcuts } from '@/features/canvas/utils/canvas-shortcuts';
import { NodeFormattingToolbar, FreedrawFormattingToolbar } from './node-formatting-toolbar';
import type { FreedrawState } from './node-formatting-toolbar';
import { HoldToUnlockButton } from '../../primitives/actions';
import {
  NODE_ACTIONS,
  type NodeAction,
  type NodeActionContext,
} from '../../wiring/actions/node-actions';
import styles from './node-floating-toolbar.module.css';

export interface GifState {
  playing: boolean;
  onToggle: () => void;
}

export interface NodeFloatingToolbarProps {
  nodeId: string;
  nodeType: NodeType;
  locked?: boolean;
  onDelete: (nodeId: string) => void;
  onDuplicate: (nodeId: string) => void;
  onToggleLock?: (nodeId: string) => void;
  /** When provided, formatting controls are rendered inline before the action buttons. */
  formattingState?: FormattingState;
  /** When provided, freedraw stroke controls are rendered inline before the action buttons. */
  freedrawState?: FreedrawState;
  /** When provided, a play/pause button is rendered before the action cluster. */
  gifState?: GifState;
  className?: string;
}

/**
 * Action IDs surfaced on the floating toolbar. STRICT SUBSET of
 * NODE_ACTIONS — z-order + copy live in the right-click menu only,
 * because the floating toolbar's footprint is small and
 * z-order is rarely a one-click selection-time gesture.
 *
 * Order here is presentational; we still re-sort against NODE_ACTIONS
 * order at render time so the toolbar respects whatever the registry
 * declares as canonical.
 */
const TOOLBAR_NODE_ACTION_IDS: ReadonlySet<NodeAction['id']> = new Set([
  'duplicate',
  'lock',
  'delete',
]);

/** Map registry IDs → canvasShortcuts label fns for the tooltip extra. */
function shortcutFor(id: NodeAction['id']): string {
  switch (id) {
    case 'copy': return canvasShortcuts.copy();
    case 'duplicate': return canvasShortcuts.duplicate();
    case 'bring-to-front': return canvasShortcuts.bringToFront();
    case 'bring-forward': return canvasShortcuts.bringForward();
    case 'send-backward': return canvasShortcuts.sendBackward();
    case 'send-to-back': return canvasShortcuts.sendToBack();
    case 'lock': return canvasShortcuts.lock();
    case 'delete': return canvasShortcuts.delete();
  }
}

function tooltipWithShortcut(label: string, shortcut: string): string {
  return shortcut ? `${label} · ${shortcut}` : label;
}

/**
 * Reusable floating context toolbar rendered above a selected canvas
 * node. Composes formatting primitives (when provided) + a 1-click
 * action cluster sourced from NODE_ACTIONS.
 *
 * When locked, swaps the entire body for a HoldToUnlockButton so the
 * only affordance is unlocking — same UX the previous hand-rolled
 * version shipped with.
 */
export function NodeFloatingToolbar({
  nodeId,
  nodeType,
  locked = false,
  onDelete,
  onDuplicate,
  onToggleLock,
  formattingState,
  freedrawState,
  gifState,
  className,
}: NodeFloatingToolbarProps) {
  if (locked && onToggleLock) {
    return (
      <div className={clsx(styles.root, className)} role="toolbar" aria-label="Node actions">
        <HoldToUnlockButton onUnlock={() => onToggleLock(nodeId)} />
      </div>
    );
  }

  // Build the per-render context once. Handlers the toolbar's subset
  // doesn't use (z-order, copy) get no-op stubs — registry's run()
  // never reaches them because TOOLBAR_NODE_ACTION_IDS filters first.
  // Lock is filtered out below if onToggleLock isn't provided (matches
  // the prior behavior where the Lock button only renders when the
  // host wires the handler).
  const noop = () => {};
  const ctx: NodeActionContext = {
    nodeId,
    locked,
    onCopy: noop,
    onDuplicate,
    onBringToFront: noop,
    onBringForward: noop,
    onSendBackward: noop,
    onSendToBack: noop,
    onToggleLock: onToggleLock ?? noop,
    onDelete,
  };

  // Render in registry order, filtered to the toolbar's subset, then
  // skip the Lock entry if no onToggleLock — matches the existing
  // "Lock button is opt-in" contract.
  const visibleActions = NODE_ACTIONS
    .filter((a) => TOOLBAR_NODE_ACTION_IDS.has(a.id))
    .filter((a) => a.available(ctx))
    .filter((a) => a.id !== 'lock' || onToggleLock !== undefined);

  return (
    <div className={clsx(styles.root, className)} role="toolbar" aria-label="Node actions">
      {gifState && (
        <>
          <Tooltip title={gifState.playing ? 'Pause' : 'Play'} placement="top">
            <IconButton
              transparent
              aria-label={gifState.playing ? 'Pause' : 'Play'}
              className={styles.btn}
              onClick={gifState.onToggle}
            >
              <Icon name={gifState.playing ? 'pause' : 'play_arrow'} size={24} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" className={styles.dividerMajor} />
        </>
      )}
      {freedrawState && (
        <>
          <FreedrawFormattingToolbar state={freedrawState} />
          <Divider orientation="vertical" className={styles.divider} />
        </>
      )}
      {formattingState && (
        <>
          <NodeFormattingToolbar nodeType={nodeType} state={formattingState} />
          <Divider orientation="vertical" className={styles.divider} />
        </>
      )}

      {visibleActions.map((action) => {
        const label = action.labelFor ? action.labelFor(ctx) : action.label;
        const iconName = action.iconFor ? action.iconFor(ctx) : action.icon;
        // Lock's hand-rolled version used the lock_open icon (showing
        // "click to lock" affordance) regardless of state — preserve
        // that visual by overriding iconFor's output for the lock
        // entry. The registry's iconFor flips lock↔lock_open based on
        // ctx.locked, but the floating toolbar reaches this branch
        // only when !locked (the locked-state HoldToUnlockButton path
        // returned early above), so we always want lock_open here.
        const renderedIcon = action.id === 'lock' ? 'lock_open' : iconName;
        return (
          <Tooltip
            key={action.id}
            title={tooltipWithShortcut(label, shortcutFor(action.id))}
            placement="top"
          >
            <IconButton
              transparent
              aria-label={label}
              className={styles.btn}
              onClick={() => action.run(ctx)}
            >
              <Icon name={renderedIcon} size={icon.md} />
            </IconButton>
          </Tooltip>
        );
      })}
    </div>
  );
}
